import type { Session } from "@supabase/supabase-js";
import { getSupabaseClient } from "./supabase-client";
import {
  clearSession,
  loadSession,
  saveSession,
  shouldRefreshAccessToken,
} from "./session-store";

let authListenerRegistered = false;

function ensureAuthListener(): void {
  const client = getSupabaseClient();
  if (authListenerRegistered) {
    return;
  }
  authListenerRegistered = true;
  
  client.auth.onAuthStateChange(async (_event, session) => {
    console.log("[AuthService] Auth state change event", {
      event: _event,
      hasSession: !!session,
      hasRefreshToken: !!session?.refresh_token,
    });

    if (session) {
      // Load existing session to preserve refresh token if needed
      const existing = await loadSession();
      
      // Preserve refresh_token if new session doesn't include it
      const sessionToSave: Session = session.refresh_token 
        ? session 
        : { ...session, refresh_token: existing?.refresh_token ?? session.refresh_token };
      
      console.log("[AuthService] Saving session from auth state change", {
        event: _event,
        hasRefreshToken: !!sessionToSave.refresh_token,
        preservedFromExisting: !!existing?.refresh_token && !session.refresh_token,
      });
      
      await saveSession(sessionToSave);
    } else if (_event === "SIGNED_OUT") {
      console.log("[AuthService] Clearing session due to SIGNED_OUT event");
      await clearSession();
    } else {
      console.log("[AuthService] Auth state change with no session, event:", _event);
    }
  });
}

export async function bootstrapSessionFromStorage(): Promise<Session | null> {
  console.log("[AuthService] bootstrapSessionFromStorage: Starting session restoration");

  const refreshed = await refreshSessionIfNeeded();
  if (!refreshed) {
    console.log("[AuthService] bootstrapSessionFromStorage: No session after refresh attempt");
    return null;
  }

  console.log("[AuthService] bootstrapSessionFromStorage: Session refreshed, verifying with getUser()");

  const client = getSupabaseClient();
  const { data, error } = await client.auth.getUser();

  if (error || !data.user) {
    console.error("[AuthService] Session verification failed:", {
      error: error?.message,
      errorName: error?.name,
      status: error?.status,
    });
    
    // Only clear on 4xx errors (auth/authz issues)
    if (error?.status && error.status >= 400 && error.status < 500) {
      console.log("[AuthService] Clearing session due to 4xx auth error");
      await clearSession();
    } else {
      console.log("[AuthService] Keeping session despite verification error (transient issue)");
    }
    return null;
  }

  console.log("[AuthService] bootstrapSessionFromStorage: Session verified successfully");
  ensureAuthListener();
  return refreshed;
}

export async function signInWithPassword(email: string, password: string): Promise<Session> {
  const client = getSupabaseClient();
  const { data, error } = await client.auth.signInWithPassword({ email, password });
  
  if (error || !data.session) {
    throw error ?? new Error("Session missing after login");
  }
  
  await saveSession(data.session);
  ensureAuthListener();
  return data.session;
}

export async function logout(): Promise<void> {
  await clearSession();
  const client = getSupabaseClient();
  await client.auth.signOut();
}

export async function sendMagicLink(email: string): Promise<void> {
  const client = getSupabaseClient();
  const { error } = await client.auth.signInWithOtp({ email });
  if (error) {
    throw error;
  }
}

export async function requireSession(): Promise<Session> {
  const session = await loadSession();
  if (!session) {
    throw new Error("Not authenticated");
  }
  return session;
}

export async function refreshSessionIfNeeded(opts?: {
  force?: boolean;
}): Promise<Session | null> {
  const client = getSupabaseClient();
  const existing = await loadSession();

  console.log("[AuthService] refreshSessionIfNeeded called", {
    hasExistingSession: !!existing,
    hasRefreshToken: !!existing?.refresh_token,
    expiresAt: existing?.expires_at,
    expiresAtDate: existing?.expires_at 
      ? new Date(existing.expires_at * 1000).toISOString() 
      : null,
    force: opts?.force,
  });

  if (!existing || !existing.refresh_token) {
    console.log("[AuthService] Cannot refresh: no session or refresh token");
    return null;
  }

  // Always set session to sync tokens with Supabase client
  await client.auth.setSession({
    access_token: existing.access_token,
    refresh_token: existing.refresh_token,
  });

  const needsRefresh = opts?.force === true || shouldRefreshAccessToken(existing);

  console.log("[AuthService] Refresh decision", {
    needsRefresh,
    reason: opts?.force ? "forced" : "token expiring soon",
  });

  if (needsRefresh) {
    const { data, error } = await client.auth.refreshSession();

    if (error || !data.session) {
      console.error("[AuthService] Session refresh failed:", {
        error: error?.message,
        hasSession: !!data?.session,
      });
      await clearSession();
      return null;
    }

    console.log("[AuthService] Session refreshed successfully", {
      newExpiresAt: data.session.expires_at,
      hasRefreshToken: !!data.session.refresh_token,
    });

    // Preserve refresh_token if new session doesn't include it
    const sessionToSave: Session = data.session.refresh_token
      ? data.session
      : { ...data.session, refresh_token: existing.refresh_token };
    
    await saveSession(sessionToSave);
    return sessionToSave;
  }

  console.log("[AuthService] Token still fresh, no refresh needed");
  return existing;
}
